/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable max-len */
import { Col, message, Row } from 'antd';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';

import AddressForm from '~/components/forms/AddressForm/AddressForm';
import CheckoutPage from '~/components/forms/CheckoutPage/CheckoutPage';
import CollectDataForm from '~/components/forms/CollectDataForm/CollectDataForm';
import ConsumerAuthenticationForm from '~/components/forms/ConsumerAuthenticationForm/ConsumerAuthenticationForm';
import OtpForm from '~/components/forms/OtpForm/OtpForm';
import PinForm from '~/components/forms/PinForm/PinForm';
import { LayoutComponent } from '~/components/Layout/Layout.component';
import { LoadingContext } from '~/contexts/LoaderContext';
import { GetPaymentResponse } from '~/entities/responses/getPaymentResponse';
import { GetPostCsPaymentResponse } from '~/entities/responses/getPostCsPaymentResponse';
import { GetPostPaymentMobileResponse } from '~/entities/responses/getPostPaymentMobileResponse';
import { GetPostPaymentResponse } from '~/entities/responses/getPostPaymentResponse';
import { GetSetupAuthResponse } from '~/entities/responses/getSetupAuthResponse';
import { GetValidatePaymentResponse } from '~/entities/responses/getValidatePaymentResponse';
import { AvailableCountry } from '~/entities/schemas/availableCountry';
import { DataTransacton } from '~/entities/schemas/dataTransaction';
import { MeansOfPayment } from '~/entities/schemas/meansOfPayment';
import { GeneralException } from '~/exceptions/generalException';
import { ABANDONED_PAGE_ROUTE, ERROR_PAGE_ROUTE } from '~/routing/routes';
import { CryptoService } from '~/services/CryptoService';
import { CyberSourceService, SetupAuthPaymentBody } from '~/services/CyberSourceService';
import { FlutterwaveCardInfos, PaymentService } from '~/services/PaymentService';
import { logger } from '~/utils/func/logger';
import { isTransactionNotFoundError } from '~/utils/paymentResultUtils';
import { appIdKey } from '~/utils/verifDataApp/appKey';
import './Home.scss';

const Home: React.FC = () => {
  const [selectedItem, setSelectedItem] = useState(0);
  const [transaction, setTransaction] = useState<GetPaymentResponse>();
  // const [publicKey, setPublicKey] = useState<string>();
  const [processingCardPayment, setProcessingCardPayment] = useState(false);
  const [processingMobilePayment, setProcessingMobilePayment] = useState(false);
  const [processingPin, setProcessingPin] = useState(false);
  const [processingOtp, setProcessingOtp] = useState(false);
  const [processingAddress, setProcessingAddress] = useState(false);
  const [otpType, setOtpType] = useState<string>('');
  const [dataTransaction, setDataTransaction] = useState<DataTransacton | null>(null);
  const [loadingDataTr, setLoadingDataTr] = useState<boolean>(false);
  // const [cardTypeSaved, setCardTypeSaved] = useState('');

  const [setupAuthResponse, setSetupAuthResponse] = useState<GetSetupAuthResponse | null>(null);
  const [setupAuthPaymentBody, setSetupAuthPaymentBody] = useState<SetupAuthPaymentBody | null>(null);

  const [csPaymentResponse, setCsPaymentResponse] = useState<GetPostCsPaymentResponse | null>(null);

  const { t } = useTranslation();
  const { push } = useHistory();

  const { onLine } = navigator;

  const { startLoading, stopLoading } = useContext(LoadingContext);

  const handleDoActionAfterPayment = useCallback(
    (response: GetPostPaymentResponse, transactionCached: GetPaymentResponse) => {
      const { status, redirect, authentication_mode: autorisation_mode } = response.data;
      switch (status) {
        case 'ACCEPTED':
          getTransactionDetails(transactionCached.data.transactionRef);
          break;
        case 'PENDING': {
          if (autorisation_mode === 'pin') {
            handleChangeHomeView(3);
          } else if (autorisation_mode === 'otp') {
            if (redirect !== null && redirect.length > 0) {
              window.location.replace(redirect);
              return;
            }
            const OTP_TYPE = 'INITIAL';
            setOtpType(OTP_TYPE);
            handleChangeHomeView(2);
          } else if (autorisation_mode === 'avs_noauth') {
            handleChangeHomeView(4);
          } else if (autorisation_mode === 'redirect') {
            if (redirect !== null) {
              window.location.replace(redirect);
            }
          }

          break;
        }

        case 'REFUSED':
          getTransactionDetails(transactionCached.data.transactionRef);
          break;

        case 'REJECTED':
          getTransactionDetails(transactionCached.data.transactionRef);
          break;

        default:
          message.error(t('home:global:generalResponse'));
          break;
      }
    },
    [],
  );

  const handleDoActionAfterCsPayment = useCallback(
    async (
      response: GetSetupAuthResponse,
      transactionCached: GetPaymentResponse,
      sensitiveData: SetupAuthPaymentBody,
    ) => {
      const { status, accessToken, referenceId, redirect, transactionRef } = response;
      setSetupAuthResponse(response);
      setSetupAuthPaymentBody(sensitiveData);
      switch (status) {
        case 'PENDING': {
          handleChangeHomeView(5); // Collect Device Data
          // await processCsPayment(sensitiveData);
          break;
        }
        case 'ACCEPTED':
          break;
        case 'REFUSED':
          message.error(t('home:global:generalResponse'));
          break;

        default:
          message.error(t('home:global:generalResponse'));
          break;
      }
    },
    [],
  );

  const processCsPayment = useCallback(async (sensitiveData: SetupAuthPaymentBody) => {
    logger.debug('processCsPayment start', { prefix: 'Home' });
    try {
      const res = await CyberSourceService.processPayment({
        transactionRef: sensitiveData.transactionRef,
        channelId: sensitiveData.channelId,
        paymentMethod: sensitiveData.paymentMethod,
        // cardInfos: {
        //   encryptedCard: sensitiveData.cardInfos?.encryptedCard as string,
        // },
        cardDetails: sensitiveData.cardDetails,
      });
      logger.debug('processCsPaymentRes log', { prefix: 'Home', data: res });
      setCsPaymentResponse(res);
      const validatePaymentBody = {
        transactionRef: sensitiveData.transactionRef,
        channelId: sensitiveData.channelId,
        paymentMethod: sensitiveData.paymentMethod,
        authorizationId: res?.data?.authenticationId,
        cardDetails: sensitiveData.cardDetails,
      };
      localStorage.setItem('validateDetails', JSON.stringify(validatePaymentBody));

      // Case Code 825
      // if (res.code === '825') {
      //   getTransactionDetails(sensitiveData.transactionRef);
      // }

      switch (res.data?.status || res.data?.Status) {
        case 'PENDING': {
          handleChangeHomeView(6);
          break;
        }

        case 'PAYMENT_ALREADY_PROCESSED': {
          getTransactionDetails(sensitiveData.transactionRef);
          break;
        }

        case 'ACCEPTED': {
          getTransactionDetails(sensitiveData.transactionRef);
          break;
        }

        case 'REFUSED': {
          getTransactionDetails(sensitiveData.transactionRef);
          break;
        }
        case 'REJECTED': {
          getTransactionDetails(sensitiveData.transactionRef);
          break;
        }
        default:
          message.error(t('home:global:generalResponse'));
          break;
      }
      logger.debug('processCsPayment', { prefix: 'Home', data: res });
    } catch (error: any) {
      const exception = error.response?.data as GeneralException;
      message.error(exception.description || t('home:global:generalResponse'), 3);
    }
  }, []);

  const handleBootPaymentForms = useCallback(
    async (formValues: any, payment?: MeansOfPayment, specificType?: string, selectedCountry?: AvailableCountry) => {
      if (specificType === 'CARD') {
        handleSubmitCardPaymentForm(formValues, payment as MeansOfPayment);
        return;
      }
      if (specificType === 'mobile') {
        handleSubmitMobilePaymentForm(formValues, payment as MeansOfPayment, selectedCountry);
        return;
      }
    },
    [],
  );

  const handleSubmitCardPaymentForm = useCallback(async (formValues: any, payment: MeansOfPayment) => {
    if (!onLine) {
      message.error(t('home:itsLookLikeYouAreNotConnected'));
      return;
    }
    const { cardHolder, cardType: paymentMethod } = formValues;

    if (paymentMethod !== 'visa' && paymentMethod !== 'mastercard') {
      message.error(t('home:cardPaymentForm:incorrectCardNumber'));
      return;
    }

    // if (paymentMethod === 'discover') {
    //   message.error(t('home:cardPaymentForm:incorrectCardNumber'));
    //   return;
    // }

    const crypt = new CryptoService();
    crypt.setSecretKey(process.env.REACT_APP_APPLI_SECKEY ?? '');
    const decryptDat = crypt.decryptData(localStorage.getItem('data') as string) as GetPaymentResponse;
    // appIdKey.includes(decryptDat.data.appId ?? '')  true : redirige sur CyberSource -
    // if (paymentMethod === 'visa' && appIdKey.includes(decryptDat.data.appId ?? '')) {
    //   handleSubmitSetupAuth(formValues, payment);
    //   return;
    // }
    const appId = decryptDat?.data?.appId ?? '';
    // console.log('Consultation Analyse ');
    // console.log(appId, 'appId');
    // console.log(
    //   appIdKey.some(a => a.appId === appId),
    //   'appIdKey status',
    // );
    if (paymentMethod === 'visa' && appId && appIdKey.some(a => a.appId === appId)) {
      handleSubmitSetupAuth(formValues, payment);
      return;
    }

    // if (paymentMethod === 'discover') {
    //   message.error(t('home:cardPaymentForm:incorrectCardNumber'));
    //   return;
    // }

    // if (paymentMethod.toLowerCase().includes('american')) {
    //   message.error(t('home:cardPaymentForm:incorrectCardNumber'));
    //   return;
    // }

    // Blocker Amex

    // setCardTypeSaved(paymentMethod);

    const loadingKey = 'loadingKey';
    setProcessingCardPayment(true);
    message.loading({
      key: loadingKey,
      content: t('home:global:paymentProcessing'),
    });
    // const publicKey = await handleGetPublicKey();

    const cryptonit = new CryptoService();
    cryptonit.setSecretKey(process.env.REACT_APP_APPLI_SECKEY ?? '');
    const decryptData = cryptonit.decryptData(localStorage.getItem('data') as string) as GetPaymentResponse;

    // const getSelfTransaction = JSON.parse(localStorage.getItem('data') as string);
    const selfTransaction = decryptData;
    const appIdClient = selfTransaction.data.appId;

    // Mastercard
    // (paymentMethod === 'visa' && !appIdKey.includes(decryptDat.data.appId ?? ''))
    if (paymentMethod === 'mastercard' || paymentMethod === 'visa') {
      try {
        const [expiry_month, expiry_year] = formValues.expirationDate.split('/');

        const cardNumber = formValues.cardNumber.replace(/\s/g, '');
        const cvv = formValues.cvv.toString();

        const sensitiveData = {
          card_number: cardNumber,
          cvv,
          card: 'card',
          expiry_month,
          expiry_year,
          currency: selfTransaction.data.currency,
          amount: String(selfTransaction.data.totalAmount), // toFixed(2)
          email: selfTransaction.data.customerEmail,
          fullname: cardHolder,
          phone_number: selfTransaction.data.customerPhoneNumber,
          tx_ref: selfTransaction.data.transactionRef,
          redirect_url: process.env.REACT_APP_API_URL + '/flutterwave/callback/return',
          // https://demo-api-paygw.dbsafricapay.com:8443
          // redirect_url: 'https://demo-api-paygw.dbsafricapay.com:8443/api/flutterwave/callback/return', // https://demo-api-paygw.dbsafricapay.com:8443
        };

        const encryptCardData = cryptonit.encryptData({ ...sensitiveData, paymentMethod });

        localStorage.setItem('cardInfos', encryptCardData);

        const crypto = new CryptoService();

        // get secret key from appIdKey dynamically
        const secretKey = appIdKey.find(a => a.appId === appIdClient)?.secretKey ?? '';
        crypto.setSecretKey(secretKey);
        // crypto.setSecretKey(process.env.REACT_APP_APPLI_SECKEY ?? '');

        // console.log(payment?.id, ' payment?.id - payment');
        // console.log('d6e5eacd-3491-4be8-9f5e-d3538a6b024b', 'placed force');
        // console.log(
        //   selfTransaction.data.meansOfPayment.find(e => e.type === 'CARD' && e.code === 'VISA_MASTERCARD')
        //     ?.id as string,
        //   'get server placed force',
        // );
        // const resEncryption = await handleEncryptCardInfos(sensitiveData);

        // const flocash = new Flocash();
        // flocash.setPublicKey(publicKey as string);
        const encryptedCard = crypto.encryptData(sensitiveData);
        // console.log(payment?.id, 'payment?.id - payment');
        // console.log('d6e5eacd-3491-4be8-9f5e-d3538a6b024b', ' master place ');
        // flocash.encrypt(sensitiveData);
        const res = await PaymentService.processPayment({
          transactionRef: selfTransaction.data.transactionRef,
          channelId: selfTransaction.data.meansOfPayment.find(e => e.type === 'CARD' && e.code === 'VISA_MASTERCARD')
            ?.id as string, // payment?.id,
          // channelId: VISA_MASTERCARD, // payment?.id,
          paymentMethod: paymentMethod.toUpperCase(),
          cardInfos: {
            encryptedCard: encryptedCard as string,
          },
          // mobileMoneyInfos: null,
        });

        setProcessingCardPayment(false);
        logger.debug('res after payment', { prefix: 'Home', data: res });
        logger.debug('selfTransaction after payment', { prefix: 'Home', data: selfTransaction });
        handleDoActionAfterPayment(res, selfTransaction);
      } catch (error: any) {
        message.destroy(loadingKey);
        const exception = error.response?.data as GeneralException;
        let finalException;

        if (exception !== null && exception !== undefined && exception.description !== null) {
          finalException = exception.description;
        }
        message.error(finalException || t('home:global:generalResponse'), 3);
        logger.debug('error', { prefix: 'Home', data: error });
        logger.debug('finalException', { prefix: 'Home', data: finalException });
        setProcessingCardPayment(false);
      }
    } else {
      message.error(t('home:cardPaymentForm:incorrectCardNumber'));
      return;
    }
  }, []);

  const handleSubmitSetupAuth = useCallback(async (formValues: any, payment: MeansOfPayment) => {
    const { cardType: paymentMethod, cardNumber, cvv } = formValues;

    logger.debug('formValues', { prefix: 'Home', data: formValues });
    logger.debug('paymentMethod card', { prefix: 'Home', data: paymentMethod });

    const loadingKey = 'loadingKey';
    setProcessingCardPayment(true);
    message.loading({
      key: loadingKey,
      content: t('home:global:paymentProcessing'),
    });
    // const publicKey = await handleGetPublicKey();

    const cryptonit = new CryptoService();
    cryptonit.setSecretKey(process.env.REACT_APP_APPLI_SECKEY ?? '');
    const decryptData = cryptonit.decryptData(localStorage.getItem('data') as string) as GetPaymentResponse;

    // const getSelfTransaction = JSON.parse(localStorage.getItem('data') as string);
    const selfTransaction = decryptData;

    try {
      const [expiry_month, expiry_year] = formValues.expirationDate.split('/');

      // console.log('expiry_year', expiry_year);

      // const options = {
      //   expirationMonth: expiry_month,
      //   expirationYear: expiry_year,
      // };

      const sensitiveData = {
        transactionRef: selfTransaction.data.transactionRef,
        channelId: payment?.id,
        paymentMethod,
        // cardInfos: {
        //   encryptedCard: '',
        // },
        cardDetails: {
          cardNumber: String(cardNumber).replaceAll(' ', ''),
          cardExpiryMonth: expiry_month,
          cardExpiryYear: expiry_year,
          cardType: paymentMethod,
          cardVV: cvv,
        },
      };

      logger.debug('sensitiveData CyberSource', { prefix: 'Home', data: sensitiveData });

      const res = await CyberSourceService.payerSetupAuth(sensitiveData);

      // console.log('res', res);
      setProcessingCardPayment(false);
      handleDoActionAfterCsPayment(res, selfTransaction, sensitiveData);
    } catch (error: any) {
      message.destroy(loadingKey);
      const exception = error.response?.data as GeneralException;
      let finalException;

      if (exception !== null && exception !== undefined && exception.description !== null) {
        finalException = exception.description;
      }
      message.error(finalException || t('home:global:generalResponse'), 3);
      setProcessingCardPayment(false);
    }
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleEncryptCardInfos = useCallback(async (cardPaymentInfos: FlutterwaveCardInfos) => {
    try {
      const res = await PaymentService.flutterwaveEncryptCard(cardPaymentInfos);
      return res;
    } catch (error: any) {
      const exception = error.response?.data as GeneralException;
      message.error(exception.description || t('home:global:generalResponse'), 3);
      logger.error('Setup auth error', error instanceof Error ? error : undefined, { prefix: 'Home', data: error });
    }
  }, []);

  const handleDoActionAfterMobilePayment = useCallback(
    (response: GetPostPaymentMobileResponse, transactionCached: GetPaymentResponse) => {
      const { status, redirect, authentication_mode: autorisation_mode } = response.data;
      switch (status) {
        case 'ACCEPTED':
          getTransactionDetails(transactionCached.data.transactionRef);
          break;
        case 'PENDING': {
          if (autorisation_mode === 'redirect') {
            if (redirect !== null) {
              window.location.replace(redirect);
            }
          } else if (autorisation_mode === 'callback') {
            getTransactionDetails(transactionCached.data.transactionRef);
          }

          break;
        }

        case 'REFUSED':
          getTransactionDetails(transactionCached.data.transactionRef);
          break;

        default:
          message.error(t('home:global:generalResponse'));
          break;
      }
    },
    [],
  );

  const handleSubmitMobilePaymentForm = useCallback(
    async (formValues: any, payment: MeansOfPayment, selectedCountry?: AvailableCountry) => {
      if (!onLine) {
        message.error(t('home:itsLookLikeYouAreNotConnected'));
        return;
      }
      const { phoneNumber } = formValues;
      if (payment === undefined) {
        message.warning(t('home:mobilePaymentForm:youMustSelectPaymentMethod'), 2);
        return;
      }

      if (phoneNumber === '') {
        message.warning(t('home:mobilePaymentForm:phoneNumberIsMandatory'), 2);
        return;
      }
      const loadingMobKey = 'loadingMobKey';
      setProcessingMobilePayment(true);
      message.loading({
        key: loadingMobKey,
        content: t('home:mobilePaymentForm:mobilePaymentProcessing'),
      });
      try {
        const crypto = new CryptoService();
        crypto.setSecretKey(process.env.REACT_APP_APPLI_SECKEY ?? '');
        const decryptData = crypto.decryptData(localStorage.getItem('data') as string) as GetPaymentResponse;

        const getSelfTransaction = decryptData; // JSON.parse(localStorage.getItem('data') as string);
        const selfTransaction = getSelfTransaction;

        const res = await PaymentService.processMobilePayment({
          transactionRef: selfTransaction.data.transactionRef,
          channelId: payment.id,
          paymentMethod: 'Mobile Money',
          mobileMoneyInfos: {
            phoneNumber,
            currency: selectedCountry?.currencycode,
            country: selectedCountry?.countrycode,
          },
        });
        setProcessingMobilePayment(false);
        handleDoActionAfterMobilePayment(res, selfTransaction);
      } catch (error: any) {
        message.destroy(loadingMobKey);
        const exception = error.response?.data as GeneralException;
        message.error(exception.description || t('home:global:generalResponse'), 3);
        setProcessingMobilePayment(false);
      }
    },
    [],
  );

  const handleSubmitCheckOtpType = useCallback(async (formValues: any, otpStatus: string) => {
    if (otpStatus === 'INITIAL') {
      handleSubmitInitialOtpForm(formValues);
    } else if (otpStatus === 'FINAL') {
      handleSubmitFinalOtpForm(formValues);
    }
  }, []);

  // Lorsque directement après le paiement le authentication_mode est otp
  const handleSubmitInitialOtpForm = useCallback(async (formValues: any) => {
    const loadingMobKey = 'loadingMobKey';
    const { otp } = formValues;
    setProcessingPin(true);
    message.loading({
      key: loadingMobKey,
      content: t('home:global:formSubmissionLoading'),
    });

    const crypto = new CryptoService();
    crypto.setSecretKey(process.env.REACT_APP_APPLI_SECKEY ?? '');
    const decryptData = crypto.decryptData(localStorage.getItem('data') as string) as GetPaymentResponse;

    const getSelfTransaction = decryptData;
    const selfTransaction = getSelfTransaction;

    try {
      const getCardInfos = crypto.decryptData(localStorage.getItem('cardInfos') as string) as FlutterwaveCardInfos;
      const selfCardInfos = getCardInfos;

      const resEncryption = await PaymentService.flutterwaveEncryptCard({
        ...selfCardInfos,
        authorization: {
          mode: 'otp',
          otp,
        },
      });

      const encryptedCard = resEncryption?.data.publicKey;

      const res = await PaymentService.validatePayment({
        transactionRef: selfTransaction.data.transactionRef,
        channelId: selfTransaction.data.meansOfPayment.find(e => e.type === 'CARD' && e.active === true)?.id as string,
        paymentMethod: selfCardInfos.paymentMethod, // cardTypeSaved.toUpperCase()
        cardInfos: {
          encryptedCard: encryptedCard as string,
        },
        // mobileMoneyInfos: null,
      });

      handleDoActionAfterPinValidation(res);
    } catch (error: any) {
      message.destroy(loadingMobKey);
      const exception = error.response?.data as GeneralException;
      message.error(exception.description || t('home:global:generalResponse'), 3);
      setProcessingPin(false);
    }
  }, []);

  // après authentification supplémentaire (pin, avs_noauth) et réencryptage , ensuite rédirection ici
  const handleSubmitFinalOtpForm = useCallback(async (formValues: any) => {
    const { otp } = formValues;
    const loadingMobKey = 'loadingMobKey';
    setProcessingOtp(true);
    message.loading({
      key: loadingMobKey,
      content: t('home:global:formSubmissionLoading'),
    });

    try {
      const crypto = new CryptoService();
      crypto.setSecretKey(process.env.REACT_APP_APPLI_SECKEY ?? '');
      const decryptData = crypto.decryptData(localStorage.getItem('data') as string) as GetPaymentResponse;
      logger.debug('decryptData', { prefix: 'Home', data: decryptData });
      const getSelfTransaction = decryptData;
      const selfTransaction = getSelfTransaction;
      const res = await PaymentService.finalValidatePayment({
        otp,
        transactionRef: selfTransaction.data.transactionRef,
      });

      // Get Transactions details with transactionRef from api
      if (res.data.status === 'ACCEPTED') {
        await getTransactionDetails(res.data.transactionRef);
      } else if (res.data.status === 'REFUSED') {
        await getTransactionDetails(res.data.transactionRef);
      } else if (res.data.status === 'REJECTED') {
        await getTransactionDetails(res.data.transactionRef);
      }
    } catch (error: any) {
      message.destroy(loadingMobKey);
      const exception = error.response?.data as GeneralException;
      message.error(exception.description || t('home:global:generalResponse'), 3);
      setProcessingOtp(false);
    }
  }, []);

  // const handleSubmitInitialOtpForm = useCallback(async (formValues: any) => {
  //   const { otp } = formValues;
  //   const loadingMobKey = 'loadingMobKey';
  //   setProcessingOtp(true);
  //   message.loading({
  //     key: loadingMobKey,
  //     content: t('home:global:formSubmissionLoading'),
  //   });
  // }, []);

  const handleSubmitPinForm = useCallback(async (formValues: any) => {
    const loadingMobKey = 'loadingMobKey';
    const { pin } = formValues;
    setProcessingPin(true);
    message.loading({
      key: loadingMobKey,
      content: t('home:global:formSubmissionLoading'),
    });

    const crypto = new CryptoService();
    crypto.setSecretKey(process.env.REACT_APP_APPLI_SECKEY ?? '');
    const decryptData = crypto.decryptData(localStorage.getItem('data') as string) as GetPaymentResponse;

    const getSelfTransaction = decryptData;
    const selfTransaction = getSelfTransaction;

    try {
      // const getCardInfos = JSON.parse(localStorage.getItem('cardInfos') as string);
      // const selfCardInfos = getCardInfos as FlutterwaveCardInfos;

      const getCardInfos = crypto.decryptData(localStorage.getItem('cardInfos') as string) as FlutterwaveCardInfos;
      const selfCardInfos = getCardInfos;

      const resEncryption = await PaymentService.flutterwaveEncryptCard({
        ...selfCardInfos,
        authorization: {
          mode: 'pin',
          pin,
        },
      });

      const encryptedCard = resEncryption?.data.publicKey;

      const res = await PaymentService.validatePayment({
        transactionRef: selfTransaction.data.transactionRef,
        // channelId: selfTransaction.data.meansOfPayment.find(e => e.operator.active === true)?.id as string,
        channelId: selfTransaction.data.meansOfPayment.find(e => e.type === 'CARD' && e.active === true)?.id as string,
        paymentMethod: selfCardInfos.paymentMethod,
        cardInfos: {
          encryptedCard: String(encryptedCard),
        },
        // mobileMoneyInfos: null,
      });

      handleDoActionAfterPinValidation(res);
    } catch (error: any) {
      message.destroy(loadingMobKey);
      const exception = error.response?.data as GeneralException;
      message.error(exception.description || t('home:global:generalResponse'), 3);
      setProcessingPin(false);
    }
  }, []);

  const handleDoActionAfterPinValidation = useCallback(async (res: GetValidatePaymentResponse) => {
    const {
      data: { transactionRef, status, redirect },
    } = res;

    // localStorage.setItem('validateDetailsYves', JSON.stringify(res));
    const OTP_TYPE = 'FINAL';
    switch (status) {
      case 'pending':
        if (redirect !== null && redirect.length > 0) {
          window.location.replace(redirect);
          return;
        }
        setOtpType(OTP_TYPE);
        handleChangeHomeView(2);
        break;

      case 'PENDING':
        if (redirect !== null && redirect.length > 0) {
          window.location.replace(redirect);
          return;
        }
        setOtpType(OTP_TYPE);
        handleChangeHomeView(2);
        break;

      case 'ACCEPTED':
        await getTransactionDetails(transactionRef);
        break;

      case 'successful':
        await getTransactionDetails(transactionRef);
        break;

      case 'REFUSED':
        await getTransactionDetails(transactionRef);
        break;

      case 'failed':
        await getTransactionDetails(transactionRef);
        break;

      case 'REJECTED':
        await getTransactionDetails(transactionRef);
        break;

      default:
        message.error(t('home:global:generalResponse'));
        await getTransactionDetails(transactionRef);
        break;
    }
    // switch (status) {
    //   case 'pending':
    //     if (redirect !== null && redirect.length > 0) {
    //       window.location.replace(redirect);
    //       return;
    //     }
    //     const OTP_TYPE = 'FINAL';
    //     setOtpType(OTP_TYPE);
    //     handleChangeHomeView(2);
    //     break;

    //   case 'successful':
    //     await getTransactionDetails(transactionRef);
    //     break;

    //   case 'failed':
    //     await getTransactionDetails(transactionRef);
    //     break;
    //   default:
    //     message.error(t('home:global:generalResponse'));
    //     break;
    // }
  }, []);

  const getTransactionDetails = useCallback(async (transactionRef: string) => {
    startLoading();
    try {
      const res = await PaymentService.getTransactionDetails(transactionRef);
      const {
        data,
        data: {
          transaction: { status },
        },
      } = res;

      if (data !== null && data !== undefined) {
        switch (status) {
          case 'ACCEPTED':
            window.location.reload(); // push(SUCCESS_PAGE_ROUTE());
            break;
          case 'REFUSED': // REJECTED
            window.location.reload();
            // push(FAIL_PAGE_ROUTE());
            break;
          case 'PENDING':
            window.location.reload();
            break;
          default:
            window.location.reload();
            break;
        }
      } else {
        push(ERROR_PAGE_ROUTE());
      }
      stopLoading();
    } catch (error: unknown) {
      stopLoading();
      if (isTransactionNotFoundError(error)) {
        push(ABANDONED_PAGE_ROUTE());
        return;
      }
      const exception = (error as { response?: { data?: GeneralException } })?.response?.data as GeneralException;
      message.error(exception?.description || t('home:global:generalResponse'), 3);
    }
  }, []);

  const handleSubmitAddress = useCallback(async (formValues: any) => {
    const { citySelected, cityEnter, address, state, country, zipcode } = formValues;

    let city = '';

    // if (citySelected !== '' && cityEnter !== '') {
    //   message.error(t('addressForm:bothCityTypeSelectedError'), 3);
    //   return;
    // }

    if (cityEnter !== '') {
      city = cityEnter;
    }

    // if (citySelected !== '') {
    //   city = citySelected;
    // }

    if (city.length === 0) {
      message.error(t('addressForm:cityRequired'), 2);
      return;
    }

    const loadingMobKey = 'loadingMobKey';
    setProcessingAddress(true);
    message.loading({
      key: loadingMobKey,
      content: t('home:global:formSubmissionLoading'),
    });

    const crypto = new CryptoService();
    crypto.setSecretKey(process.env.REACT_APP_APPLI_SECKEY ?? '');
    const decryptData = crypto.decryptData(localStorage.getItem('data') as string) as GetPaymentResponse;

    const getSelfTransaction = decryptData;
    const selfTransaction = getSelfTransaction;
    try {
      const getCardInfos = crypto.decryptData(localStorage.getItem('cardInfos') as string) as FlutterwaveCardInfos;
      const selfCardInfos = getCardInfos;
      const appIdClient = selfTransaction.data.appId;
      // const getCardInfos = JSON.parse(localStorage.getItem('cardInfos') as string);
      // const selfCardInfos = getCardInfos as FlutterwaveCardInfos;

      const cardInfos = {
        card_number: selfCardInfos.card_number,
        cvv: selfCardInfos.cvv,
        card: selfCardInfos.card,
        expiry_month: selfCardInfos.expiry_month,
        expiry_year: selfCardInfos.expiry_year,
        currency: selfCardInfos.currency,
        amount: selfCardInfos.amount,
        email: selfCardInfos.email,
        fullname: selfCardInfos.fullname,
        phone_number: selfTransaction.data.customerPhoneNumber,
        tx_ref: selfCardInfos.tx_ref,
        redirect_url: selfCardInfos.redirect_url,
      } as FlutterwaveCardInfos;

      const cryptoFlut = new CryptoService();
      const secretKey = appIdKey.find(a => a.appId === appIdClient)?.secretKey ?? '';
      cryptoFlut.setSecretKey(secretKey);
      // cryptoFlut.setSecretKey(process.env.REACT_APP_EVISA_SECKEY ?? '');

      const resEncryptCardData = cryptoFlut.encryptData({
        ...cardInfos,
        authorization: {
          mode: 'avs_noauth',
          city,
          address,
          state,
          country,
          zipcode,
        },
      });

      // const resEncryption = await PaymentService.flutterwaveEncryptCard({
      //   ...cardInfos,
      //   authorization: {
      //     mode: 'avs_noauth',
      //     city,
      //     address,
      //     state,
      //     country,
      //     zipcode,
      //   },
      // });

      // const encryptedCard = resEncryption?.data.publicKey;
      // console.log(resEncryptCardData, 'resEncryptCardData log');
      // console.log(selfCardInfos.paymentMethod, 'selfCardInfos.paymentMethod log');

      const res = await PaymentService.validatePayment({
        transactionRef: selfTransaction.data.transactionRef,
        // channelId: selfTransaction.data.meansOfPayment.find(e => e.operator.name === 'VISA/MASTERCARD')?.id as string,
        // channelId: selfTransaction.data.meansOfPayment.find(e => e.type === 'CARD' && e.active === true)?.id as string,
        channelId: selfTransaction.data.meansOfPayment.find(e => e.type === 'CARD' && e.code === 'VISA_MASTERCARD')
          ?.id as string,
        paymentMethod: selfCardInfos.paymentMethod,
        cardInfos: {
          encryptedCard: resEncryptCardData as string,
        },
      });

      handleDoActionAfterPinValidation(res);
    } catch (error: any) {
      message.destroy(loadingMobKey);
      const exception = error.response?.data as GeneralException;
      let finalException;

      if (exception !== null && exception !== undefined && exception.description !== null) {
        finalException = exception.description;
      }
      message.error(finalException || t('home:global:generalResponse'), 3);
      setProcessingAddress(false);
    }
  }, []);

  const homeViews = useMemo(
    () => [
      {
        view: (
          <CheckoutPage
            paymentInfos={transaction as GetPaymentResponse}
            selectedItem={selectedItem}
            handleSubmit={handleBootPaymentForms}
            loading={processingCardPayment || processingMobilePayment}
            processingMobilePayment={processingMobilePayment}
            handleSubmitMobilePaymentForm={handleSubmitMobilePaymentForm}
            loadingDataTr={loadingDataTr}
            dataTransaction={dataTransaction}
          />
        ),
      },
      {
        view: (
          <div></div>
          // <MobilePaymentForm
          //   paymentInfos={transaction}
          //   selectedItem={selectedItem}
          //   handleSubmit={handleSubmitMobilePaymentForm}
          //   loading={processingMobilePayment}
          // />
        ),
      },
      {
        view: (
          <Row justify="center">
            <Col lg={12}>
              <OtpForm
                selectedItem={selectedItem}
                handleSubmit={(formValues: any) => handleSubmitCheckOtpType(formValues, otpType)}
                loading={processingOtp}
              />
            </Col>
          </Row>
        ),
      },
      {
        view: (
          <Row justify="center">
            <Col lg={12}>
              <PinForm selectedItem={selectedItem} handleSubmit={handleSubmitPinForm} loading={processingPin} />
            </Col>
          </Row>
        ),
      },
      {
        view: (
          <Row justify="center">
            <Col lg={12}>
              <AddressForm selectedItem={selectedItem} handleSubmit={handleSubmitAddress} loading={processingAddress} />
            </Col>
          </Row>
        ),
      },
      {
        view: (
          <Row justify="center">
            <Col lg={12}>
              <CollectDataForm
                redirect={setupAuthResponse?.redirect ?? ''}
                accessToken={setupAuthResponse?.accessToken ?? ''}
                setupAuthPaymentBody={setupAuthPaymentBody as SetupAuthPaymentBody}
                pay={processCsPayment}
              />
            </Col>
          </Row>
        ),
      },
      {
        view: (
          <Row justify="center">
            <Col lg={12}>
              <ConsumerAuthenticationForm
                redirect={csPaymentResponse?.data?.redirect ?? ''}
                accessToken={csPaymentResponse?.data?.accessToken ?? ''}
                setupAuthPaymentBody={setupAuthPaymentBody}
              />
            </Col>
          </Row>
        ),
      },
    ],
    [
      transaction,
      selectedItem,
      processingCardPayment,
      processingMobilePayment,
      processingOtp,
      processingPin,
      processingAddress,
      dataTransaction,
      loadingDataTr,
    ],
  );

  const handleChangeHomeView = useCallback(
    (index: number) => {
      if (!homeViews?.length) return;
      setSelectedItem(index);
    },
    [homeViews],
  );

  const setDefaultHomeView = useCallback(() => {
    if (!homeViews[0]) return;
    setSelectedItem(0);
  }, [homeViews]);

  const handleGetTransactionsInfos = useCallback(() => {
    const crypto = new CryptoService();
    crypto.setSecretKey(process.env.REACT_APP_APPLI_SECKEY ?? '');
    const decryptData = crypto.decryptData(localStorage.getItem('data') as string) as GetPaymentResponse;

    const getTransaction = decryptData;
    setTransaction(getTransaction);
  }, []);

  const handleGetDataTransaction = useCallback(async () => {
    setLoadingDataTr(true);
    try {
      const crypto = new CryptoService();
      crypto.setSecretKey(process.env.REACT_APP_APPLI_SECKEY ?? '');
      const decryptData = crypto.decryptData(localStorage.getItem('data') as string) as GetPaymentResponse;
      // console.log(decryptData, 'res.data regarding transaction decryptData');
      const getTransaction = decryptData;
      const getTr = getTransaction as GetPaymentResponse;
      const res = await PaymentService.getTransactionApp(getTr.data.transactionRef);
      setDataTransaction(res.data);
      // console.log(res.data, 'res.data regarding transaction');
      setLoadingDataTr(false);
    } catch (error: any) {
      setLoadingDataTr(false);
      const exception = error.response?.data as GeneralException;
      let finalException;

      if (exception !== null && exception !== undefined && exception.description !== null) {
        finalException = exception.description;
      }
      message.error(finalException || t('home:global:generalResponse'), 3);
    }
  }, []);

  useEffect(() => {
    handleGetTransactionsInfos();
    setDefaultHomeView();
    handleGetDataTransaction();
  }, []);

  return (
    <LayoutComponent dataTransaction={dataTransaction} loadingDataTr={loadingDataTr}>
      {homeViews[selectedItem].view}
    </LayoutComponent>
  );
};

export default Home;
